import React, { useState, useEffect } from "react";
import {
  FaGraduationCap,
  FaChartLine,
  FaUsers,
  FaLightbulb,
  FaBuilding,
  FaHandshake,
  FaAward,
  FaCheckCircle,
} from "react-icons/fa";

const About = () => {
  const [counters, setCounters] = useState({
    students: 0,
    faculty: 0,
    placement: 0,
    trades: 0,
  });

  useEffect(() => {
    const animateNumbers = () => {
      const targets = { students: 500, faculty: 15, placement: 90, trades: 2 };
      const duration = 2000;
      const stepTime = 20;
      const steps = duration / stepTime;
      let currentStep = 0;

      const interval = setInterval(() => {
        currentStep++;
        const progress = currentStep / steps;
        setCounters({
          students: Math.min(Math.floor(targets.students * progress), targets.students),
          faculty: Math.min(Math.floor(targets.faculty * progress), targets.faculty),
          placement: Math.min(Math.floor(targets.placement * progress), targets.placement),
          trades: Math.min(Math.floor(targets.trades * progress), targets.trades),
        });
        if (currentStep >= steps) clearInterval(interval);
      }, stepTime);
    };
    animateNumbers();
  }, []);

  const milestones = [
    { year: "2010", title: "Institute Established", desc: "Started with 2 trades and 50 students" },
    { year: "2015", title: "NSQF Alignment", desc: "Courses aligned with national skill framework" },
    { year: "2018", title: "Placement Cell", desc: "Dedicated placement cell established" },
    { year: "2022", title: "1000+ Alumni", desc: "Over 1000 students successfully trained" },
  ];

  const coreValues = [
    { icon: FaHandshake, title: "Integrity", desc: "Honest and ethical practices" },
    { icon: FaLightbulb, title: "Innovation", desc: "Embracing new technologies" },
    { icon: FaUsers, title: "Inclusivity", desc: "Equal opportunities for all" },
    { icon: FaAward, title: "Excellence", desc: "Striving for the best" },
  ];

  return (
    <section className="w-full overflow-hidden bg-gradient-to-br from-green-50 via-white to-orange-50 py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 text-center animate-fadeInUp">
          <div className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 shadow-md">
            <FaGraduationCap className="h-5 w-5 text-green-600" />
            <span className="text-sm font-semibold text-gray-700">Est. 2010 | Govt. Recognized</span>
          </div>
          <h1 className="mt-6 text-3xl font-extrabold text-gray-900 md:text-5xl">
            About <span className="text-green-600">ITI Bhaupur</span>
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-base text-gray-600 md:text-lg">
            Empowering youth through skill development and technical education since 2010
          </p>
        </div>
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-8 mb-16 items-stretch">
          <div className="space-y-6">
            <div className="rounded-2xl bg-white p-6 shadow-lg transition-all hover:shadow-xl md:p-8 group">
              <p className="text-base leading-relaxed text-gray-700 md:text-lg">
                It is our wishful thinking to visualize 100% literacy in the nation by
                fulfilling our sacred duty of providing a platform which is very essential
                and aims at multi‑dimensional objectives to take youngsters to glorious
                heights in different fields and transforming them into tomorrow's
                entrepreneurs who will be proficient in their respective fields.
              </p>
              <div className="mt-4 flex justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="text-sm text-green-600">— Management, ITI Bhaupur</span>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="group relative overflow-hidden rounded-xl bg-white p-5 shadow-md transition-all hover:shadow-xl hover:-translate-y-1">
                <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="rounded-full bg-green-100 p-2 text-green-600 w-fit mb-3">
                  <FaLightbulb className="h-5 w-5" />
                </div>
                <h3 className="font-bold text-gray-800 text-lg">Our Vision</h3>
                <p className="text-sm text-gray-600 mt-1">
                  To become a center of excellence in technical education, nurturing
                  skilled professionals who drive economic growth.
                </p>
              </div>
              <div className="group relative overflow-hidden rounded-xl bg-white p-5 shadow-md transition-all hover:shadow-xl hover:-translate-y-1">
                <div className="absolute inset-0 bg-gradient-to-r from-orange-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="rounded-full bg-orange-100 p-2 text-orange-500 w-fit mb-3">
                  <FaChartLine className="h-5 w-5" />
                </div>
                <h3 className="font-bold text-gray-800 text-lg">Our Mission</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Provide quality training, foster innovation, and build industry‑ready
                  professionals through modern infrastructure and expert faculty.
                </p>
              </div>
            </div>
          </div>
          <div className="relative h-full flex flex-col">
            <div className="flex-1 overflow-hidden rounded-2xl shadow-xl group">
              <img
                src="image6.png"
                alt="Students at ITI Bhaupur"
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
            </div>
            <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg p-3 flex items-center gap-2 animate-bounce-slow">
              <FaUsers className="h-4 w-4 text-green-600" />
              <span className="text-sm font-bold text-gray-800">500+ Students</span>
            </div>
            <div className="absolute -top-4 -right-4 bg-white rounded-xl shadow-lg p-3 flex items-center gap-2 animate-bounce-slow animation-delay-200">
              <FaAward className="h-4 w-4 text-orange-500" />
              <span className="text-sm font-bold text-gray-800">90% Placement</span>
            </div>
          </div>
        </div>
        <div className="mb-16">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-2xl bg-white p-6 text-center shadow-md transition-all hover:scale-105 hover:shadow-xl group">
              <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-green-600 group-hover:bg-green-600 group-hover:text-white transition-colors">
                <FaGraduationCap className="h-7 w-7" />
              </div>
              <p className="text-3xl font-bold text-gray-800">{counters.students}+</p>
              <p className="text-sm text-gray-500">Students Trained</p>
            </div>
            <div className="rounded-2xl bg-white p-6 text-center shadow-md transition-all hover:scale-105 hover:shadow-xl group">
              <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-orange-100 text-orange-500 group-hover:bg-orange-500 group-hover:text-white transition-colors">
                <FaUsers className="h-7 w-7" />
              </div>
              <p className="text-3xl font-bold text-gray-800">{counters.faculty}+</p>
              <p className="text-sm text-gray-500">Expert Faculty</p>
            </div>
            <div className="rounded-2xl bg-white p-6 text-center shadow-md transition-all hover:scale-105 hover:shadow-xl group">
              <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-green-600 group-hover:bg-green-600 group-hover:text-white transition-colors">
                <FaChartLine className="h-7 w-7" />
              </div>
              <p className="text-3xl font-bold text-gray-800">{counters.placement}%</p>
              <p className="text-sm text-gray-500">Placement Rate</p>
            </div>
            <div className="rounded-2xl bg-white p-6 text-center shadow-md transition-all hover:scale-105 hover:shadow-xl group">
              <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-orange-100 text-orange-500 group-hover:bg-orange-500 group-hover:text-white transition-colors">
                <FaBuilding className="h-7 w-7" />
              </div>
              <p className="text-3xl font-bold text-gray-800">{counters.trades}+</p>
              <p className="text-sm text-gray-500">Industry Trades</p>
            </div>
          </div>
        </div>
        <div className="mb-16">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-extrabold text-gray-900 md:text-4xl">
              Our <span className="text-orange-500">Journey</span>
            </h2>
            <p className="mt-2 text-gray-600">Key milestones since establishment</p>
          </div>
          <div className="relative">
            <div className="absolute left-1/2 top-0 h-full w-1 -translate-x-1/2 bg-gradient-to-b from-green-500 to-orange-500 hidden md:block"></div>
            <div className="space-y-8 md:space-y-0">
              {milestones.map((item, idx) => (
                <div key={idx} className={`relative flex flex-col md:flex-row ${idx % 2 === 0 ? 'md:justify-start' : 'md:justify-end'} items-center`}>
                  <div className={`md:w-1/2 ${idx % 2 === 0 ? 'md:pr-12 text-right' : 'md:pl-12 text-left'} w-full mb-4 md:mb-0`}>
                    <div className="rounded-xl bg-white p-5 shadow-md transition-all hover:shadow-xl hover:scale-105">
                      <div className="inline-block rounded-full bg-green-100 px-3 py-1 text-sm font-semibold text-green-700 mb-2">
                        {item.year}
                      </div>
                      <h3 className="font-bold text-gray-800 text-lg">{item.title}</h3>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                  <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 hidden md:block">
                    <div className="h-4 w-4 rounded-full bg-gradient-to-r from-green-500 to-orange-500 ring-4 ring-white"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="mb-16">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-extrabold text-gray-900 md:text-4xl">
              Our Core <span className="text-green-600">Values</span>
            </h2>
            <p className="mt-2 text-gray-600">The principles that guide us</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {coreValues.map((value, idx) => (
              <div key={idx} className="group rounded-2xl bg-white p-6 text-center shadow-md transition-all hover:shadow-xl hover:-translate-y-2">
                <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-r from-green-500 to-orange-500 text-white">
                  <value.icon className="h-7 w-7" />
                </div>
                <h3 className="font-bold text-gray-800 text-lg">{value.title}</h3>
                <p className="text-sm text-gray-600 mt-1">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl bg-gradient-to-r from-green-50 to-orange-50 p-8 shadow-md mb-16">
          <div className="grid gap-8 md:grid-cols-2 items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-800 md:text-3xl">
                World-Class <span className="text-green-600">Infrastructure</span>
              </h2>
              <ul className="mt-4 space-y-3">
                {[
                  "Modern workshops with latest machinery",
                  "Fully equipped electrical labs",
                  "Computer lab with high-speed internet",
                  "Library with technical books & journals",
                  "Separate hostel facilities for outstation students",
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2 group hover:translate-x-1 transition">
                    <FaCheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <img
                src="image7.png"
                alt="Workshop"
                className="rounded-lg shadow-md hover:scale-105 transition h-40 w-full object-cover"
              />
              <img
                src="image1.png"
                alt="Lab"
                className="rounded-lg shadow-md hover:scale-105 transition h-40 w-full object-cover mt-6"
              />
            </div>
          </div>
        </div>
        <div className="rounded-3xl bg-gradient-to-r from-green-600 to-orange-500 p-8 text-center text-white shadow-xl">
          <h3 className="text-2xl font-bold md:text-3xl">Join Our Journey of Excellence</h3>
          <p className="mt-2 text-lg opacity-90">Admissions open for the 2024-25 session. Limited seats available.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-4">
            <button className="rounded-full bg-white px-6 py-2 font-semibold text-green-600 shadow-md transition hover:scale-105">
              Apply Now
            </button>
            <button className="rounded-full border border-white px-6 py-2 font-semibold transition hover:bg-white hover:text-green-600">
              Download Brochure
            </button>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes bounceSlow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        .animate-fadeInUp {
          animation: fadeInUp 0.8s ease-out forwards;
        }
        .animate-bounce-slow {
          animation: bounceSlow 3s ease-in-out infinite;
        }
        .animation-delay-200 {
          animation-delay: 0.2s;
        }
      `}</style>
    </section>
  );
};

export default About;